/**
 * The interactive forecast map's vector line art -- isolines, wind barbs and
 * fronts -- as plain MapLibre layer specs (step 4 S4-2). Every feature of a
 * frame's GeoJSON carries `layer`, its config-layer index, matching the
 * manifest's `vector.layers[].index`; each descriptor renders through a
 * per-type stack filtered on it. The webapp hands the specs to react-map-gl,
 * the mobile app to MapLibre RN; each adds its own source and anchor.
 *
 * Valid for both engines: array-typed properties are arrays (MapLibre
 * Native's Android bridge crashes on a scalar where the spec wants an
 * array), and no layer reads `zoom`.
 */
import type {ForecastMapVectorLayerSpec} from '../../api/types';
import {barbIconExpression, whiteBarbIconExpression} from './windBarbs';

/** One MapLibre layer, without its source and anchor (the app's). */
export type ForecastVectorLayerStyle = {
  id: string;
  type: 'line' | 'symbol';
  filter: unknown[];
  layout: {visibility: 'visible' | 'none'} & Record<string, unknown>;
  paint?: Record<string, unknown>;
};

// Map line art: a white halo keeps labels legible over any data fill, in
// both themes.
const LABEL_HALO = '#ffffff';

/**
 * The layers for one frame's source, in creation order. Contour labels go
 * last (top of the vector block): symbol placement runs top-most layer
 * first, so isoline labels win collision against the barb grid instead of
 * being culled by it.
 *
 * `visible` is false for the neighbour frames kept mounted (hidden) around
 * the current one; `disabledLayers` holds the config-layer indexes the user
 * switched off. `whiteBarbs` picks the white glyph set for the saturated
 * wind-speed raster (core `windSpeedRasterPainted`).
 */
export const forecastVectorLayerSpecs = ({
  sourceId,
  layers,
  visible,
  disabledLayers,
  whiteBarbs,
}: {
  sourceId: string;
  layers: readonly ForecastMapVectorLayerSpec[];
  visible: boolean;
  disabledLayers: readonly number[];
  whiteBarbs: boolean;
}): ForecastVectorLayerStyle[] => {
  const visibilityOf = (spec: ForecastMapVectorLayerSpec) =>
    visible && !disabledLayers.includes(spec.index) ? 'visible' : 'none';
  const ofLayer = (spec: ForecastMapVectorLayerSpec) => [
    '==',
    ['get', 'layer'],
    spec.index,
  ];
  const specs: ForecastVectorLayerStyle[] = [];
  for (const spec of layers) {
    const idPrefix = `${sourceId}-l${spec.index}`;
    const visibility = visibilityOf(spec);
    if (spec.type === 'contour') {
      specs.push({
        id: `${idPrefix}-line`,
        type: 'line',
        filter: ofLayer(spec),
        layout: {visibility},
        paint: {'line-color': ['get', 'color'], 'line-width': 1.2},
      });
    } else if (spec.type === 'barbs') {
      specs.push({
        id: `${idPrefix}-barbs`,
        type: 'symbol',
        filter: ofLayer(spec),
        layout: {
          visibility,
          'icon-image': whiteBarbs
            ? whiteBarbIconExpression
            : barbIconExpression,
          'icon-rotate': ['get', 'dir'],
          'icon-rotation-alignment': 'map',
          // The backend emits barbs on a fixed geographic grid, so
          // collision culling is what thins them as the grid compresses at
          // low zoom. Lower sort key places first and wins placement --
          // strongest winds survive.
          'icon-allow-overlap': false,
          'icon-ignore-placement': false,
          // The style spec's `padding` type: one to four numbers, a bare
          // number read as all four (`validate_padding.ts`). Array form,
          // which MapLibre Native's Android bridge requires.
          'icon-padding': [4, 4, 4, 4],
          'symbol-sort-key': ['*', -1, ['get', 'kt']],
        },
      });
    } else {
      // fronts: typed polylines + H/L letter points.
      specs.push(
        {
          id: `${idPrefix}-front-lines`,
          type: 'line',
          filter: [
            'all',
            ofLayer(spec),
            ['==', ['geometry-type'], 'LineString'],
          ],
          layout: {visibility},
          paint: {'line-color': ['get', 'color'], 'line-width': 2.5},
        },
        {
          id: `${idPrefix}-front-centers`,
          type: 'symbol',
          filter: ['all', ofLayer(spec), ['==', ['geometry-type'], 'Point']],
          layout: {
            visibility,
            'text-field': ['get', 'letter'],
            'text-font': ['Noto Sans Bold'],
            'text-size': 20,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': ['get', 'color'],
            'text-halo-color': LABEL_HALO,
            'text-halo-width': 2,
          },
        },
      );
    }
  }
  for (const spec of layers) {
    if (spec.type !== 'contour') {
      continue;
    }
    specs.push({
      id: `${sourceId}-l${spec.index}-label`,
      type: 'symbol',
      filter: ofLayer(spec),
      layout: {
        visibility: visibilityOf(spec),
        'symbol-placement': 'line',
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 10,
        'text-max-angle': 30,
        'text-padding': 12,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': LABEL_HALO,
        'text-halo-width': 1.2,
      },
    });
  }
  return specs;
};
